import { useCallback, useMemo } from "react";
import { MEASUREMENT_COLUMNS } from "@/constants/operating-points-constants";

/**
 * Hook for detecting unsaved changes across operating points tabs/views.
 * Extracts checkTabHasUnsavedChanges, hasUnsavedChanges, getUnsavedTabs,
 * unsavedViewNames, and hasAnyUnsavedChanges into a single concern.
 *
 * @param {Object} params
 * @param {Array<{ id: string, label: string }>} params.tabs - Tab definitions
 * @param {string} params.activeTab - Active tab id
 * @param {Object.<string, Array>} params.customColumns - Current column state per tab
 * @param {Object.<string, Array>} params.savedColumns - Last-saved column state per tab
 * @returns {Object} Unsaved-change helpers and derived state
 */
export function useUnsavedOperatingPointChanges({
  tabs,
  activeTab,
  customColumns,
  savedColumns,
}) {
  const checkTabHasUnsavedChanges = useCallback(
    (tabId) => {
      if (!tabId) return false;

      if (tabId.startsWith("custom-")) {
        // For custom views, compare with saved state (order matters for column order)
        const currentCols = customColumns[tabId] || [];
        const savedCols = savedColumns[tabId] || [];

        // If lengths differ, there are changes
        if (currentCols.length !== savedCols.length) {
          return true;
        }

        // Compare arrays in order so column order is considered (labelName + tagName per index)
        const toOrderedSignature = (cols) =>
          cols.map((col) => ({
            labelName: col.labelName,
            tagName: col.tagName,
            universalDescriptorId: col.universalDescriptorId ?? null,
          }));

        const currentSig = toOrderedSignature(currentCols);
        const savedSig = toOrderedSignature(savedCols);

        return (
          currentSig.length !== savedSig.length
          || currentSig.some(
            (c, i) =>
              c.labelName !== savedSig[i]?.labelName
              || c.tagName !== savedSig[i]?.tagName
              || c.universalDescriptorId !== savedSig[i]?.universalDescriptorId
          )
        );
      } else {
        // For default views, check if there are any custom column modifications
        const customCols = customColumns[tabId] || [];
        const defaultCols = MEASUREMENT_COLUMNS[tabId] || [];

        // If there are any custom columns, there are changes
        if (customCols.length > 0) {
          return true;
        }

        // Also check if any default columns have been modified
        // (custom columns with same keys as defaults indicate modifications)
        const defaultKeys = new Set(defaultCols.map((col) => col.key));
        const hasModifiedDefaults = customCols.some((col) =>
          defaultKeys.has(col.key)
        );

        return hasModifiedDefaults;
      }
    },
    [customColumns, savedColumns]
  );

  const hasUnsavedChanges = useMemo(() => {
    return checkTabHasUnsavedChanges(activeTab);
  }, [activeTab, checkTabHasUnsavedChanges]);

  const getUnsavedTabs = useCallback(() => {
    return tabs.filter((tab) => checkTabHasUnsavedChanges(tab.id));
  }, [tabs, checkTabHasUnsavedChanges]);

  const unsavedViewNames = useMemo(() => {
    return getUnsavedTabs().map((tab) => tab.label);
  }, [getUnsavedTabs]);

  const hasAnyUnsavedChanges = useMemo(() => {
    return unsavedViewNames.length > 0;
  }, [unsavedViewNames]);

  return {
    checkTabHasUnsavedChanges,
    hasUnsavedChanges,
    getUnsavedTabs,
    unsavedViewNames,
    hasAnyUnsavedChanges,
  };
}
